import Controller from '@ember/controller';
import { action } from '@ember/object';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { task } from 'ember-concurrency';
import type { IntlService } from 'ember-intl';
import type { Mutation, MutationUpdateMeArgs, User } from 'graphql-models';
import type { AppUserSettingsModel } from 'walden-trace-app/routes/app/user-settings';
import type ApiService from 'walden-trace-app/services/api';
import updateMeMutation from 'walden-trace-app/graphql/user/update-me.graphql';
import type RouterService from '@ember/routing/router-service';
import type { GraphQLFormattedError } from 'graphql';

export default class AppUserSettingsController extends Controller {
  declare model: AppUserSettingsModel;
  declare 'formValues.preferredLanguage': User['preferredLanguage'];

  @service('api') declare apiService: ApiService;
  @service('intl') declare intlService: IntlService;
  @service('router') declare routerService: RouterService;

  @tracked formValues: Partial<User> = {
    ...this.user,
    notificationPreferences:
      typeof this.user.notificationPreferences === 'string' &&
      this.user.notificationPreferences !== null &&
      this.user.notificationPreferences !== undefined
        ? (JSON.parse(this.user.notificationPreferences) as Record<string, unknown>)
        : {},
  };
  @tracked errorMessage: string | null = null;

  get user() {
    return this.model.user;
  }

  get noPhoneWithTextMessageActiveWarning() {
    const phone = this.formValues.phone as string;
    const notificationPreferences = this.formValues.notificationPreferences as {
      [key: string]: unknown;
    };

    const textMessagePreferences = Object.entries(notificationPreferences).filter(
      ([, value]) => Array.isArray(value) && value.includes('sms'),
    );

    return !phone && textMessagePreferences.length > 0;
  }

  @action
  saveChanges(event: Event) {
    event.preventDefault();
    void this.updateMe.perform();
  }

  @action
  updatePhone(phone: string) {
    this.formValues = {
      ...this.formValues,
      phone: phone,
    };
  }

  @action
  updateNotificationPreference(type: string, value: unknown) {
    this.formValues = {
      ...this.formValues,
      notificationPreferences: {
        ...(this.formValues.notificationPreferences as Record<string, unknown>),
        [type]: value,
      },
    };
  }

  updateMe = task(async () => {
    try {
      await this.apiService.mutate<
        {
          updateMe: Mutation['updateMe'];
        },
        MutationUpdateMeArgs
      >({
        mutation: updateMeMutation,
        variables: {
          id: this.user.id,
          input: {
            phone: this.formValues.phone as string,
            preferredLanguage: this.formValues.preferredLanguage as string,
            notificationPreferences: JSON.stringify(this.formValues.notificationPreferences),
          },
        },
      });
      this.routerService.refresh('application');
    } catch (e) {
      console.error('Failed to update user', e, typeof e);
      this.errorMessage =
        (e as { errors: GraphQLFormattedError[] }).errors[0]?.message ||
        this.intlService.t('errors.generic');
      throw e;
    }
  });
}
